import styled from "styled-components";

import { windowWidth } from "@styles/global";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const PageTitle = styled.div`
  padding-top: 5px;
  font-size: 27px;
  color: ${(props) => props.theme.brand.ink};

  @media ( max-width: ${windowWidth.laptop.large} ) {
    font-size: 23px;
  }
`;

export const PageSubtitle = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.brand.muted};
`;

export const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SearchBox = styled.div`
  width: 250px;
`;

export const PrimaryButton = styled.button`
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(props) => props.theme.colors.primary};
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${(props) => props.theme.brand.navyHover};
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.brand.line};
  color: ${(props) => props.theme.brand.navy};
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #c0cfe0;
    background: #f5f8fb;
  }
`;

const listGridColumns = "minmax(0, 1fr) 280px 110px 90px 90px 90px";

export const ListCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 53, 99, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ListHeader = styled.div`
  display: grid;
  grid-template-columns: ${listGridColumns};
  gap: 12px;
  align-items: center;
  padding: 13px 20px;
  border-bottom: 1px solid #eef1f5;
  background: #fafbfd;

  span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(props) => props.theme.brand.soft};
  }
`;

export const ListRow = styled.div`
  display: grid;
  grid-template-columns: ${listGridColumns};
  gap: 12px;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f3f7;

  &:last-child {
    border-bottom: none;
  }
`;

export const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

interface RowTitleProps {
  $expired: boolean;
}

export const RowTitle = styled.span<RowTitleProps>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$expired ? props.theme.brand.muted : props.theme.brand.ink)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowPreview = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ReachLine = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${(props) => props.theme.brand.navyMed};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PeriodCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  span {
    font-size: 12.5px;
    color: #3a4a5c;
    white-space: nowrap;
  }
`;

export const AuthorLine = styled.span`
  font-size: 11px !important;
  color: ${(props) => props.theme.brand.soft} !important;
`;

interface StatusChipProps {
  $status: "active" | "scheduled" | "expired";
}

const statusColors = {
  active: { color: "#2e9c62", background: "rgba(70, 193, 125, 0.14)" },
  scheduled: { color: "#b97f00", background: "rgba(255, 173, 0, 0.16)" },
  expired: { color: "#8a9ab0", background: "#eef1f5" }
};

export const StatusChip = styled.span<StatusChipProps>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => statusColors[props.$status].color};
  background: ${(props) => statusColors[props.$status].background};
  padding: 4px 10px;
  border-radius: 5px;
`;

export const RightAligned = styled.span`
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #3a4a5c;
`;

/**
 * Both counters open the same interaction listing. Reads matter on their own:
 * an announcement without a link can never be clicked, so the clicks column
 * would leave its audience unreachable.
 */
export const InteractionsButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.brand.navyMed};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.brand.navyHover};
  }
`;

export const ActionsCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 4px;

  .MuiIconButton-root {
    color: ${(props) => props.theme.brand.navy};
  }
`;

export const Hint = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
`;
